/**
 * Metrics — platform health numbers, AI usage, publishing stats
 */
import React, { useCallback } from "react";
import { metrics as metricsApi } from "../api.js";
import { useAsync } from "../hooks.js";

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="ops-stat">
      <div className="ops-stat-value" style={accent ? { color: accent } : {}}>{value}</div>
      <div className="ops-stat-label">{label}</div>
    </div>
  );
}

export function MetricsView() {
  const overview = useAsync<any>(useCallback(() => metricsApi.overview(), []), []);
  const tenantM  = useAsync<any>(useCallback(() => metricsApi.tenants(), []), []);
  const aiM      = useAsync<any>(useCallback(() => metricsApi.ai(), []), []);
  const publishM = useAsync<any>(useCallback(() => metricsApi.publishing(), []), []);

  const d  = overview.data;
  const t  = tenantM.data;
  const ai = aiM.data;
  const p  = publishM.data;

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <h1 className="ops-page-title">Platform Metrics</h1>
      </div>

      <p className="ops-card-title">Tenants</p>
      <div className="ops-stat-grid">
        <Stat label="Total"     value={d?.tenants?.total     ?? "—"} />
        <Stat label="Active"    value={d?.tenants?.active    ?? "—"} />
        <Stat label="Trialing"  value={d?.tenants?.trialing  ?? "—"} accent="var(--ops-info)" />
        <Stat label="Paying"    value={d?.tenants?.paying    ?? "—"} accent="var(--ops-success)" />
        <Stat label="Suspended" value={d?.tenants?.suspended ?? "—"}
          accent={d?.tenants?.suspended > 0 ? "var(--ops-warning)" : undefined} />
      </div>

      <div className="ops-grid-2" style={{ gap: "1.25rem", marginBottom: "1.5rem" }}>
        <div className="ops-card">
          <p className="ops-card-title">AI Usage — today</p>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <Stat label="Messages"    value={d?.ai?.messagesToday    ?? "—"} />
            <Stat label="Generations" value={d?.ai?.generationsToday ?? "—"} />
            <Stat label="Reviews"     value={d?.ai?.reviewsToday     ?? "—"} />
          </div>
        </div>
        <div className="ops-card">
          <p className="ops-card-title">Publishing</p>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <Stat label="Live sites"      value={d?.publishing?.totalPublished ?? "—"} />
            <Stat label="Published today" value={d?.publishing?.publishedToday ?? "—"} />
            <Stat label="Failures (24h)"  value={d?.publishing?.failuresLast24h ?? 0}
              accent={d?.publishing?.failuresLast24h > 0 ? "var(--ops-danger)" : undefined} />
          </div>
        </div>
      </div>

      {t && (
        <>
          <p className="ops-card-title">Tenant Growth — last 30 days</p>
          <div className="ops-card" style={{ marginBottom: "1.5rem", overflowX: "auto" }}>
            <table className="ops-table" style={{ minWidth: 400 }}>
              <thead><tr><th>Day</th><th>New tenants</th></tr></thead>
              <tbody>
                {(t.growth30d ?? []).map((row: any) => (
                  <tr key={row.day}>
                    <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>{row.day}</td>
                    <td><strong>{row.n}</strong></td>
                  </tr>
                ))}
                {(t.growth30d ?? []).length === 0 && (
                  <tr><td colSpan={2} className="ops-empty">No data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {ai && (
        <>
          <p className="ops-card-title">AI Capability Breakdown — last 7 days</p>
          <div className="ops-table-wrap" style={{ marginBottom: "1.5rem" }}>
            <table className="ops-table">
              <thead><tr><th>Capability</th><th>Event type</th><th>Requests</th><th>Units</th></tr></thead>
              <tbody>
                {(ai.byCapability ?? []).map((row: any, i: number) => (
                  <tr key={i}>
                    <td><code style={{ fontSize: "0.75rem" }}>{row.capability ?? "—"}</code></td>
                    <td style={{ color: "var(--ops-muted)", fontSize: "0.75rem" }}>{row.event_type}</td>
                    <td>{row.requests}</td>
                    <td>{row.units}</td>
                  </tr>
                ))}
                {(ai.byCapability ?? []).length === 0 && (
                  <tr><td colSpan={4} className="ops-empty">No AI usage recorded in the last 7 days.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {p && (p.recentFailures ?? []).length > 0 && (
        <>
          <p className="ops-card-title" style={{ color: "var(--ops-danger)" }}>Recent Publish Failures</p>
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead><tr><th>Tenant</th><th>Action</th><th>Time</th></tr></thead>
              <tbody>
                {p.recentFailures.map((row: any, i: number) => (
                  <tr key={i}>
                    <td><code style={{ fontSize: "0.75rem" }}>{row.tenant_id}</code></td>
                    <td style={{ fontSize: "0.8125rem" }}>{row.action}</td>
                    <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                      {row.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
